import React from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface Props {
  query: string;
  onQueryChange: (query: string) => void;
  /** Position of the current match, from 1; 0 while there is none. */
  current: number;
  total: number;
  /** The current match as a reader names it: ticker, trade id and section (a
   * trade can sit in two). */
  match: string | undefined;
  onStep: (by: 1 | -1) => void;
}

const stepButton =
  'rounded p-1 text-lr-muted hover:bg-lr-surface-2 hover:text-lr-fg disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lr-accent';

/**
 * The trade document's own search. The document draws only what is in view, so
 * the browser's Cmd+F finds nothing further down; this field searches the whole
 * document and the document scrolls the match into view.
 *
 * Cmd/Ctrl+K focuses it, as on Trades: Cmd+F stays the browser's. Escape clears
 * a query without reaching the host, so the preview modal does not close on it.
 */
export const DocumentSearch: React.FC<Props> = ({
  query,
  onQueryChange,
  current,
  total,
  match,
  onStep,
}) => {
  const input = React.useRef<HTMLInputElement>(null);
  const counter =
    total === 0
      ? 'No matches'
      : `${current.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`;
  const searching = query.trim() !== '';

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // The letter K on a Latin layout (Dvorak and Colemak included); on any
      // other, such as Russian, the key where QWERTY has it.
      const isK = /^[a-z]$/i.test(e.key)
        ? e.key.toLowerCase() === 'k'
        : e.code === 'KeyK';
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || !isK) return;
      // Another field keeps its own Cmd+K, as on Trades.
      const el = document.activeElement;
      if (
        el !== input.current &&
        (el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement)
      ) {
        return;
      }
      // Nor is the focus taken out of a dialog over the document (Mark paid):
      // it traps Tab and Escape inside itself. The preview's own dialog holds
      // the field.
      if (
        Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
          (dialog) => !dialog.contains(input.current)
        )
      ) {
        return;
      }
      e.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="flex items-center gap-1" role="search">
      <div className="relative">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-lr-faint"
          aria-hidden="true"
        />
        <input
          ref={input}
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            // An input method's own Enter and Escape: neither ours nor the
            // host's (Safari marks the committing Enter by key code 229 only).
            if (e.nativeEvent.isComposing || e.keyCode === 229) {
              e.stopPropagation();
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              onStep(e.shiftKey ? -1 : 1);
            } else if (e.key === 'Escape' && query) {
              e.stopPropagation();
              onQueryChange('');
            }
          }}
          placeholder="TRD-, ticker or ISIN"
          aria-label="Search the document"
          aria-keyshortcuts="Meta+K Control+K"
          // The browser's own clear button sits where the padding starts,
          // between the query and the counter; ours is at the edge.
          className={`w-64 rounded-md border border-lr-border py-1.5 pl-8 text-sm text-lr-fg placeholder:text-lr-faint focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 [&::-webkit-search-cancel-button]:hidden ${
            searching ? 'pr-36' : query ? 'pr-8' : 'pr-2'
          }`}
        />
        {/* Inside the field, as the Trades search shows its count: the hosts
            align the field right, and a counter beside it would move it under
            the caret as it came and grew. `pr-36` holds "25,000 of 25,000"
            and the clear button. For the eye only: the live region below
            reads it out. */}
        {searching && (
          <span
            className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 whitespace-nowrap text-[12px] tabular-nums text-lr-muted"
            aria-hidden="true"
          >
            {counter}
          </span>
        )}
        {query && (
          <button
            type="button"
            onClick={() => {
              onQueryChange('');
              input.current?.focus();
            }}
            className={`absolute right-1 top-1/2 -translate-y-1/2 ${stepButton}`}
            aria-label="Clear search"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onStep(-1)}
        disabled={total === 0}
        className={stepButton}
        aria-label="Previous match"
      >
        <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => onStep(1)}
        disabled={total === 0}
        className={stepButton}
        aria-label="Next match"
      >
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      {/* Always mounted: a status region announces a change of its text, not
          its own arrival. */}
      <span role="status" className="sr-only">
        {searching ? (match ? `${counter}: ${match}` : counter) : ''}
      </span>
    </div>
  );
};
